/**
 * `<DriftBadge>` — builder-ssot-consolidation Phase 5 Session 5.7 (ADR 0019
 * Decision B).
 *
 * Inline informational badge rendered alongside a tool-result card when any
 * value the tool recorded (`result.written[k]`) no longer matches the current
 * SSOT value (`inputs[k].value`). A later edit superseded what this tool wrote.
 *
 * Drive signal: `written` vs `inputs[k].value` — NOT `meta.revision` (ADR 0019).
 * Composite `set_table_inputs` keys (`a.b.c`) are skipped because they are NOT
 * in `inputs` (they carry no named-range entry). This is correct: the badge
 * no-ops for `set_table_inputs` by design (ADR 0019 Decision B).
 *
 * Token compliance: `text-text-muted` only — mirrors `ApplyMarketDefaultsBadge`
 * exactly. `--color-text-subtle` does NOT exist (verified index.css:34-35;
 * research Phase 5 § 8 D2). Zero raw hex.
 *
 * Copy: "superseded by later edit" — user-safe phrasing (does NOT name internal
 * concepts: "drift", "agent_state", "provenance"). Per feedback_user_facing_copy.
 */

interface DriftBadgeProps {
  /** Raw named-range key → value map the tool recorded (tool result `written`). */
  written: Record<string, unknown> | undefined;
  /** Current SSOT input records from `useConversationViews().data.inputs`. */
  inputs: Record<string, { value: unknown }> | undefined;
}

/**
 * Renders when any `written[k]` value no longer equals `inputs[k].value`.
 * Returns null when all values match, when either prop is undefined, or when
 * no `written` keys have a corresponding `inputs` entry (e.g. all composite).
 */
export function DriftBadge({ written, inputs }: DriftBadgeProps) {
  if (!written || !inputs) return null;

  const drifted = Object.entries(written).filter(([k, v]) => {
    const cur = inputs[k];
    if (!cur) return false; // composite key / not a scalar input — skip
    return !Object.is(cur.value, v); // shallow value compare (same as Object.is)
  });

  if (drifted.length === 0) return null;

  const tooltipLines = drifted
    .map(
      ([k, v]) =>
        `${k}: recorded ${String(v)} → current ${String(inputs[k]?.value)}`
    )
    .join('\n');

  return (
    <span className="text-xs font-mono text-text-muted" title={tooltipLines}>
      superseded by later edit
      {drifted.length > 1 ? `s (${drifted.length})` : ''}
    </span>
  );
}
